import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2, Upload, X, RotateCw } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * OwnVoiceRecorder — lets stations use their OWN voice instead of AI TTS.
 *
 * Two modes inside this card:
 *   1. Record-in-browser via MediaRecorder API (mic → WebM/Opus blob)
 *   2. Upload pre-recorded MP3 / WAV / M4A / OGG file
 *
 * Both pathways POST to /api/imaging/assets/upload with kind=voice,
 * source=user_recorded|user_uploaded — the returned asset bubbles up
 * via the onAsset(asset) prop so the parent VoiceTab can flow it into
 * the existing "Send to Now Playing" + mix-with-bed pipeline.
 */
export default function OwnVoiceRecorder({ mode, onAsset }) {
  // shared
  const [voiceLabel, setVoiceLabel] = useState("");
  const [busy, setBusy] = useState(false);

  // record state
  const [recording, setRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const mediaRef = useRef(null); // MediaRecorder
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const startTsRef = useRef(0);

  // upload state
  const [chosenFile, setChosenFile] = useState(null);

  // Cleanup on unmount or mode switch
  useEffect(() => {
    return () => {
      if (recordedUrl) URL.revokeObjectURL(recordedUrl);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (timerRef.current) clearInterval(timerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // When mode flips, clear local takes/files so the user starts clean
    discardTake();
    setChosenFile(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // ============================================================
  // RECORD MODE
  // ============================================================
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000,
        },
      });
      streamRef.current = stream;
      chunksRef.current = [];
      // Try opus first (Chrome/FF); fallback to default
      const mime =
        MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        setRecordedBlob(blob);
        setRecordedUrl(URL.createObjectURL(blob));
        if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      };
      mediaRef.current = rec;
      rec.start();
      startTsRef.current = Date.now();
      setElapsedMs(0);
      timerRef.current = setInterval(() => setElapsedMs(Date.now() - startTsRef.current), 100);
      setRecording(true);
    } catch (e) {
      toast.error(
        e?.name === "NotAllowedError"
          ? "Microphone permission denied — allow access in browser settings"
          : `Could not start recording: ${e?.message || "mic unavailable"}`
      );
    }
  };

  const stopRecording = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setRecording(false);
  };

  const discardTake = () => {
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedBlob(null);
    setRecordedUrl(null);
    setElapsedMs(0);
  };

  const saveRecording = async () => {
    if (!recordedBlob) return;
    setBusy(true);
    try {
      const ext = (recordedBlob.type.includes("ogg") ? "ogg"
        : recordedBlob.type.includes("mp4") ? "m4a"
        : "webm");
      // Browsers commonly produce webm/opus — backend doesn't accept webm.
      // Decode → re-encode as WAV in-browser before upload so pydub on the
      // server can ingest it cleanly. WAV PCM is universally supported.
      const wavBlob = await blobToWav(recordedBlob);
      const file = new File([wavBlob], `own-voice-${Date.now()}.wav`, { type: "audio/wav" });
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "voice");
      fd.append("label", voiceLabel || `Own voice take (${formatMs(elapsedMs)})`);
      fd.append("source", "user_recorded");
      fd.append("voice_label", voiceLabel || "Own voice");
      fd.append("normalize", "true");
      const { data } = await api.post("/imaging/assets/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Own-voice take saved — review & send to Now Playing");
      onAsset && onAsset(data);
      discardTake();
      setVoiceLabel("");
      // unused vars suppress
      void ext;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save recording");
    } finally {
      setBusy(false);
    }
  };

  // ============================================================
  // UPLOAD MODE
  // ============================================================
  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 250 * 1024 * 1024) { toast.error("File must be ≤ 250 MB"); return; }
    setChosenFile(f);
  };

  const uploadFile = async () => {
    if (!chosenFile) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", chosenFile);
      fd.append("kind", "voice");
      fd.append("label", voiceLabel || chosenFile.name);
      fd.append("source", "user_uploaded");
      fd.append("voice_label", voiceLabel || "Own voice");
      fd.append("normalize", "true");
      const { data } = await api.post("/imaging/assets/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Own-voice file uploaded — review & send to Now Playing");
      onAsset && onAsset(data);
      setChosenFile(null);
      setVoiceLabel("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  // ============================================================
  return (
    <div className="space-y-4">
      <div>
        <Label className="text-xs uppercase tracking-wider text-ink-secondary">Voice label (optional)</Label>
        <Input
          value={voiceLabel}
          onChange={(e) => setVoiceLabel(e.target.value.slice(0, 80))}
          placeholder='e.g. "DJ Mike — main station ID"'
          className="mt-1 bg-bg border-line"
          data-testid="own-voice-label"
        />
      </div>

      {mode === "record" && (
        <div className="rounded-xl border border-line bg-bg-elevated p-5 space-y-4" data-testid="own-voice-record">
          {!recording && !recordedBlob && (
            <div className="text-center space-y-3 py-4">
              <div className="mx-auto h-14 w-14 rounded-full bg-brand-amber/10 border border-brand-amber/40 flex items-center justify-center">
                <Mic className="h-6 w-6 text-brand-amber" />
              </div>
              <div className="text-sm">Record straight from your microphone</div>
              <div className="text-[11px] text-ink-muted">
                We&apos;ll normalize the audio before mixing.
              </div>
              <button
                onClick={startRecording}
                className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-5 py-2.5 text-sm font-semibold text-black amber-glow"
                data-testid="own-voice-record-start"
              >
                <Mic className="h-4 w-4" /> Start recording
              </button>
            </div>
          )}

          {recording && (
            <div className="text-center space-y-3 py-2">
              <div className="mx-auto h-14 w-14 rounded-full bg-red-500/15 border border-red-500/60 flex items-center justify-center animate-pulse">
                <span className="h-3 w-3 rounded-full bg-red-500" />
              </div>
              <div className="font-mono text-xl tabular-nums text-brand-amber" data-testid="own-voice-record-timer">
                {formatMs(elapsedMs)}
              </div>
              <div className="text-[11px] text-ink-muted">Recording… speak into your mic</div>
              <button
                onClick={stopRecording}
                className="inline-flex items-center gap-2 rounded-md border border-red-500/60 bg-red-500/10 px-5 py-2.5 text-sm font-semibold text-red-300 hover:bg-red-500/20"
                data-testid="own-voice-record-stop"
              >
                <Square className="h-4 w-4" /> Stop
              </button>
            </div>
          )}

          {!recording && recordedBlob && (
            <div className="space-y-3">
              <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber">
                // TAKE · {formatMs(elapsedMs)}
              </div>
              <audio src={recordedUrl} controls className="w-full" data-testid="own-voice-take-player" />
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={saveRecording}
                  disabled={busy}
                  className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-4 py-2 text-xs font-semibold text-black hover:bg-brand-amber/90 disabled:opacity-50"
                  data-testid="own-voice-save"
                >
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  {busy ? "Saving…" : "Save take"}
                </button>
                <button
                  onClick={discardTake}
                  className="inline-flex items-center gap-2 rounded-md border border-line bg-bg px-4 py-2 text-xs hover:bg-bg-elevated"
                  data-testid="own-voice-discard"
                >
                  <X className="h-3.5 w-3.5" /> Discard
                </button>
                <button
                  onClick={() => { discardTake(); startRecording(); }}
                  className="inline-flex items-center gap-2 rounded-md border border-line bg-bg px-4 py-2 text-xs hover:bg-bg-elevated"
                  data-testid="own-voice-redo"
                >
                  <RotateCw className="h-3.5 w-3.5" /> Re-record
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {mode === "upload" && (
        <div className="rounded-xl border border-line bg-bg-elevated p-5 space-y-4" data-testid="own-voice-upload">
          <label
            htmlFor="own-voice-file"
            className="block rounded-lg border border-dashed border-brand-amber/40 bg-brand-amber/5 p-8 text-center cursor-pointer hover:bg-brand-amber/10 transition"
          >
            <Upload className="h-6 w-6 text-brand-amber mx-auto mb-2" />
            <div className="text-sm font-medium">
              {chosenFile ? chosenFile.name : "Click to pick an audio file"}
            </div>
            <div className="text-[11px] text-ink-muted mt-1">
              MP3 · WAV · M4A · OGG · FLAC · AAC — max 250 MB
            </div>
          </label>
          <input
            id="own-voice-file"
            type="file"
            accept="audio/*,.mp3,.wav,.m4a,.aac,.flac,.ogg"
            onChange={onPick}
            className="hidden"
            data-testid="own-voice-file-input"
          />
          {chosenFile && (
            <div className="flex items-center justify-between rounded-md border border-line bg-bg p-3 text-xs">
              <div className="truncate">
                <span className="text-ink-secondary">Selected:</span>{" "}
                <span className="text-ink-primary font-medium">{chosenFile.name}</span>
                <span className="text-ink-muted ml-2">({(chosenFile.size / 1024 / 1024).toFixed(2)} MB)</span>
              </div>
              <button
                onClick={() => setChosenFile(null)}
                className="text-ink-muted hover:text-ink-primary"
                data-testid="own-voice-file-clear"
              ><X className="h-3.5 w-3.5" /></button>
            </div>
          )}
          <button
            onClick={uploadFile}
            disabled={!chosenFile || busy}
            className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-brand-amber px-4 py-2.5 text-sm font-semibold text-black hover:bg-brand-amber/90 disabled:opacity-50"
            data-testid="own-voice-upload-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {busy ? "Uploading…" : "Upload voice file"}
          </button>
        </div>
      )}
    </div>
  );
}

function formatMs(ms) {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

// Decode any browser-recorded blob (typically webm/opus) into a WAV file
// that the FastAPI backend can probe with pydub. Uses Web Audio API.
async function blobToWav(blob) {
  const arrayBuf = await blob.arrayBuffer();
  const AC = window.AudioContext || window.webkitAudioContext;
  const ac = new AC();
  const buf = await ac.decodeAudioData(arrayBuf.slice(0));
  const wav = audioBufferToWav(buf);
  try { ac.close(); } catch { /* noop */ }
  return new Blob([wav], { type: "audio/wav" });
}

// Minimal AudioBuffer → 16-bit PCM WAV encoder. Mono or stereo.
function audioBufferToWav(buffer) {
  const numCh = Math.min(2, buffer.numberOfChannels);
  const sr = buffer.sampleRate;
  const len = buffer.length * numCh * 2 + 44;
  const ab = new ArrayBuffer(len);
  const view = new DataView(ab);
  let p = 0;
  const wstr = (s) => { for (let i = 0; i < s.length; i++) view.setUint8(p++, s.charCodeAt(i)); };
  const w16 = (v) => { view.setUint16(p, v, true); p += 2; };
  const w32 = (v) => { view.setUint32(p, v, true); p += 4; };
  wstr("RIFF"); w32(len - 8); wstr("WAVE");
  wstr("fmt "); w32(16); w16(1); w16(numCh); w32(sr); w32(sr * numCh * 2); w16(numCh * 2); w16(16);
  wstr("data"); w32(len - 44);
  const channels = [];
  for (let i = 0; i < numCh; i++) channels.push(buffer.getChannelData(i));
  const frames = buffer.length;
  for (let i = 0; i < frames; i++) {
    for (let c = 0; c < numCh; c++) {
      let sample = Math.max(-1, Math.min(1, channels[c][i]));
      sample = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
      view.setInt16(p, sample, true); p += 2;
    }
  }
  return ab;
}
